import React, { useState, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames/bind';
import { connect } from 'react-redux';

import CategoryFilters from './CategoryFilters';
import QuickFilters from './QuickFilters';
import styles from './index.pcss';
import Filters from './QuickFilters/Filters';
import { getCurrentStore } from '../../../selectors/metaData';

const cx = classNames.bind(styles);

const TopFilter = props => {
  const {
    currentStore = '',
    facets = {},
    visualFiltersConfig = {},
    visualFilterConfigForStore = {},
    replaceInHistory,
    pushToHistory,
    fetchNextFacets,
    updateFilterModal = () => {}
  } = props;

  const { compoundFilterEnabled = false, quickFilters = false } = visualFilterConfigForStore || {};
  const { visualFilters = [], quickFilters: quickFiltersData = [] } = facets;

  const fleek = String(currentStore).toLowerCase() === 'ajiogram';

  const [isVisible, setIsVisible] = useState(true);
  const [isFixed, setIsFixed] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [placeholderHeight, setPlaceholderHeight] = useState(0);

  // NOTE: FILTER MODALS DISPLAY STATE
  const [selectedFilterTabCode, setSelectedFilterTabCode] = useState('');
  const [displaySortModal, setDisplaySortModal] = useState(false);
  const [displayOtherFilterModal, setDisplayOtherFilterModal] = useState(false);

  const hasFilters =
    (compoundFilterEnabled && visualFilters.length > 0) ||
    quickFilters;

  const containerRef = useRef<HTMLDivElement>(null);
  const originalOffsetTop = useRef(0);
  const headerHeightRef = useRef(0);
  const lastScrollY = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  const inlineFiltersObserver = useRef<IntersectionObserver | null>(null);
  const isInlineFiltersInViewRef = useRef(false);

  // Calculate total height of all sticky/fixed elements at the top of viewport
  const calculateStickyHeight = useCallback(() => {
    if (!containerRef.current) return 0;

    // Collect all sticky/fixed elements that are at the top of viewport
    const stickyElements: Array<{ top: number; bottom: number; el: Element }> = [];

    const allElements = document.querySelectorAll('*');
    allElements.forEach(el => {
      // Skip the TopFilters container itself
      if (el === containerRef.current || containerRef.current?.contains(el)) return;

      const style = window.getComputedStyle(el);
      const position = style.position;

      if (position === 'sticky' || position === 'fixed') {
        const rect = el.getBoundingClientRect();

        // Only count elements that are visible and positioned near the top
        if (rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden') {
          // Check if element is stuck at the top (top position is small, typically 0 or near 0)
          const topValue = parseFloat(style.top) || 0;

          // For elements stuck at top of viewport
          if (rect.top >= 0 && rect.top <= 100 && topValue >= 0 && topValue <= 100) {
            stickyElements.push({
              top: rect.top,
              bottom: rect.bottom,
              el
            });
          }
        }
      }
    });

    // Explicitly account for priceRevealPLPBanner — its sticky `top` can exceed the 100px
    // threshold above (e.g. 110px when dynamicAppInstallBanners is ON). Only include it when
    // it is actually stuck (rect.top ≈ CSS top value); otherwise it is still in normal flow
    // and has a large rect.top that would produce unwanted extra spacing.
    const priceRevealBanner = document.getElementById('priceRevealPLPBanner');
    if (priceRevealBanner) {
      const rect = priceRevealBanner.getBoundingClientRect();
      const bannerCSSTop = parseFloat(window.getComputedStyle(priceRevealBanner).top) || 0;
      if (rect.height > 0 && rect.top >= 0 && rect.top <= bannerCSSTop + 5) {
        stickyElements.push({ top: rect.top, bottom: rect.bottom, el: priceRevealBanner });
      }
    }

    if (stickyElements.length === 0) return 0;

    // Sort by top position
    stickyElements.sort((a, b) => a.top - b.top);

    // Calculate total height accounting for overlapping elements
    let maxBottom = 0;
    stickyElements.forEach(item => {
      if (item.bottom > maxBottom) {
        maxBottom = item.bottom;
      }
    });

    return maxBottom;
  }, []);

  useEffect(() => {
    // Initial calculation of sticky height
    const height = calculateStickyHeight();
    headerHeightRef.current = height;
    setHeaderHeight(height);

    // Store original position of TopFilters
    if (containerRef.current) {
      originalOffsetTop.current = containerRef.current.offsetTop;
    }

    // Recalculate on resize
    const handleResize = () => {
      const h = calculateStickyHeight();
      headerHeightRef.current = h;
      setHeaderHeight(h);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateStickyHeight]);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const prevScrollY = lastScrollY.current;
    lastScrollY.current = currentScrollY;

    if (currentScrollY === prevScrollY) return;

    const scrollingDown = currentScrollY > prevScrollY;

    const currentStickyHeight = calculateStickyHeight();
    if (currentStickyHeight !== headerHeightRef.current) {
      headerHeightRef.current = currentStickyHeight;
      setHeaderHeight(currentStickyHeight);
    }

    const shouldBeFixed = currentScrollY > originalOffsetTop.current - currentStickyHeight;
    setIsFixed(shouldBeFixed);

    // Not scrolled past the filter's natural position — keep it visible in normal flow
    if (!shouldBeFixed) {
      setIsVisible(true);
      return;
    }

    // InlineFilters in view always takes priority — hide TopFilters
    if (isInlineFiltersInViewRef.current) {
      const inlineEl = document.querySelector('[data-component="inline-filters"]');
      if (!inlineEl) {
        isInlineFiltersInViewRef.current = false;
      } else {
        setIsVisible(false);
        return;
      }
    }

    if (scrollingDown) {
      // While scrolling down, stay visible until 4 products have scrolled past
      const allMatches = document.querySelectorAll('[id^="product-"], [id^="jioAdsContainer"]');
      let passedCount = 0;
      for (let i = 0; i < allMatches.length; i++) {
        if (!/^(product-\d+|jioAdsContainer.*)$/.test(allMatches[i].id)) continue;
        if (allMatches[i].getBoundingClientRect().bottom <= 0) {
          passedCount++;
          if (passedCount >= 4) break;
        }
      }
      setIsVisible(passedCount < 4);
    } else {
      // Scrolling up — show sticky filters
      setIsVisible(true);
    }
  }, [calculateStickyHeight]);

  useEffect(() => {
    const handleFiltersApplied = () => {
      isInlineFiltersInViewRef.current = false;
      lastScrollY.current = 0;
      setIsFixed(false);
      setIsVisible(true);
    };

    window.addEventListener('filters-applied', handleFiltersApplied);
    return () => window.removeEventListener('filters-applied', handleFiltersApplied);
  }, []);

  useEffect(() => {
    // Set up scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Set up Intersection Observer for InlineFilters
    inlineFiltersObserver.current = new IntersectionObserver(
      entries => {
        // Check if any InlineFilters are in view
        const anyInView = entries.some(entry => entry.isIntersecting);
        isInlineFiltersInViewRef.current = anyInView;

        // If InlineFilters comes into view, hide TopFilters immediately
        if (anyInView) {
          setIsVisible(false);
        }
      },
      { threshold: 0.1 }
    );

    // Find all InlineFilters elements and observe them
    const inlineFiltersElements = document.querySelectorAll('[data-component="inline-filters"]');
    inlineFiltersElements.forEach(el => {
      inlineFiltersObserver.current?.observe(el);
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      inlineFiltersObserver.current?.disconnect();
    };
  }, [handleScroll]);

  // Recalculate positions when filters become available after async config load
  useEffect(() => {
    if (!hasFilters || !containerRef.current) return;
    const timer = setTimeout(() => {
      if (containerRef.current) {
        originalOffsetTop.current = containerRef.current.offsetTop;
      }
      const h = calculateStickyHeight();
      headerHeightRef.current = h;
      setHeaderHeight(h);
    }, 100);
    return () => clearTimeout(timer);
  }, [hasFilters, calculateStickyHeight]);

  // Track the container's natural height for the placeholder
  useEffect(() => {
    if (!containerRef.current || !hasFilters) return;
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        setPlaceholderHeight(entry.target.getBoundingClientRect().height);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [hasFilters]);

  // Re-observe InlineFilters when they might be added dynamically
  useEffect(() => {
    const observeNewInlineFilters = () => {
      const inlineFiltersElements = document.querySelectorAll('[data-component="inline-filters"]');
      if (inlineFiltersElements.length === 0) {
        isInlineFiltersInViewRef.current = false;
      }
      inlineFiltersElements.forEach(el => {
        inlineFiltersObserver.current?.observe(el);
      });
    };

    const intervalId = setInterval(observeNewInlineFilters, 1000);

    return () => clearInterval(intervalId);
  }, []);

  if (!hasFilters) return null;

  const containerStyle = isFixed ? { top: `${headerHeight}px` } : undefined;

  return (
    <>
      <div
        ref={containerRef}
        className={cx('top-filters-container', {
          hidden: isFixed && !isVisible,
          fixed: isFixed,
          ajiogram: fleek
        })}
        style={containerStyle}
      >
        {compoundFilterEnabled && <CategoryFilters compoundFilterNames={visualFiltersConfig?.compoundFilterNames || {}}/>}
        {quickFilters && (
          <QuickFilters
            visualFiltersConfig={visualFiltersConfig}
            setDisplaySortModal={setDisplaySortModal}
            setDisplayOtherFilterModal={setDisplayOtherFilterModal}
            setSelectedFilterTabCode={setSelectedFilterTabCode}
            replaceInHistory={replaceInHistory}
            updateFilterModal={updateFilterModal}
          />
        )}
      </div>
      {isFixed && placeholderHeight > 0 && <div style={{ height: placeholderHeight }} />}

      <Filters
        selectedFilterTabCode={selectedFilterTabCode}
        displayFilterModal={updateFilterModal}
        displaySortModal={displaySortModal}
        displayOtherFilterModal={displayOtherFilterModal}
        setDisplayFilterModal={updateFilterModal}
        setDisplaySortModal={setDisplaySortModal}
        setDisplayOtherFilterModal={setDisplayOtherFilterModal}
        sorts={[]}
        replaceInHistory={replaceInHistory}
        pushToHistory={pushToHistory}
        fetchNextFacets={fetchNextFacets}
      />
    </>
  );
};

function mapStateToProps(state: any) {
  const { facets = {} } = state;

  return {
    facets,
    currentStore: getCurrentStore(state)
  };
}

export default connect(mapStateToProps)(TopFilter);
